import { Badge, Button, Card, Center, Group, Loader, Stack, Text } from '@mantine/core'
import { IconCalendar, IconMessageCircle } from '@tabler/icons-react'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useWargaBooking } from '../../../../hooks/useApi'

export const Route = createFileRoute('/desa/$slug/warga/booking')({
  component: WargaBookingPage,
})

function WargaBookingPage() {
  const { data: booking, isLoading } = useWargaBooking()
  const params = useParams({ strict: false }) as { slug?: string }
  const slug = params.slug ?? ''
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    )
  }

  const aktif = booking?.filter((b: any) => ['pending', 'dikonfirmasi'].includes(b.status)) ?? []
  const riwayat = booking?.filter((b: any) => !['pending', 'dikonfirmasi'].includes(b.status)) ?? []

  return (
    <Stack gap="lg">
      {!booking?.length ? (
        <Stack align="center" py="xl" gap="sm">
          <Text c="dimmed" ta="center">
            Belum ada booking. Buat booking via chat AI atau halaman booking publik.
          </Text>
          <Group gap="sm">
            <Button
              variant="light"
              color="teal"
              size="sm"
              leftSection={<IconMessageCircle size={16} />}
              onClick={() => navigate({ to: `/desa/${slug}/warga/chat` as any })}
            >
              Chat dengan AI
            </Button>
            <Button component="a" href={`/desa/${slug}/booking`} variant="outline" size="sm" color="blue">
              Booking Publik
            </Button>
          </Group>
        </Stack>
      ) : (
        <>
          {aktif.length > 0 && (
            <Stack gap="sm">
              <Text fw={600}>Booking Aktif</Text>
              {aktif.map((b: any) => (
                <BookingCard key={b.id} booking={b} />
              ))}
            </Stack>
          )}

          {riwayat.length > 0 && (
            <Stack gap="sm">
              <Text fw={600} c="dimmed">
                Riwayat
              </Text>
              {riwayat.map((b: any) => (
                <BookingCard key={b.id} booking={b} compact />
              ))}
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}

function BookingCard({ booking, compact }: { booking: any; compact?: boolean }) {
  const tanggal = new Date(booking.tanggal)
  const now = new Date()
  const diffDays = Math.ceil((tanggal.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  const colorMap: Record<string, string> = {
    pending: 'yellow',
    dikonfirmasi: 'green',
    selesai: 'green',
    dibatalkan: 'gray',
    tidak_hadir: 'red',
  }

  const countdown =
    diffDays === 0 ? 'Hari ini' : diffDays === 1 ? 'Besok' : diffDays > 0 ? `${diffDays} hari lagi` : 'Lewat'

  return (
    <Card shadow={compact ? 'xs' : 'sm'} padding={compact ? 'sm' : 'md'} radius="md" withBorder>
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm">
          <IconCalendar size={20} color="var(--mantine-color-blue-6)" />
          <div>
            <Text size="sm" fw={compact ? 400 : 600}>
              {booking.layanan}
            </Text>
            <Text size="xs" c="dimmed">
              {tanggal.toLocaleDateString('id-ID')} · {booking.slot}
              {booking.antrian_no && ` · No. ${booking.antrian_no}`}
            </Text>
            {booking.keterangan && (
              <Text size="xs" c="dimmed">
                {booking.keterangan}
              </Text>
            )}
          </div>
        </Group>
        <Stack gap={4} align="flex-end">
          <Badge color={colorMap[booking.status] ?? 'gray'} variant="light" size="sm">
            {booking.status}
          </Badge>
          {!compact && ['pending', 'dikonfirmasi'].includes(booking.status) && (
            <Text size="xs" c={diffDays <= 1 ? 'orange' : 'dimmed'}>
              {countdown}
            </Text>
          )}
        </Stack>
      </Group>
    </Card>
  )
}
